<template>
  <!-- Hide everything when login popup is shown -->
  <template v-if="!authState.showLoginPopup.value">
    <v-btn
      v-if="!isSharedChat && isInitialized"
      icon
      class="toggle-drawer-btn"
      :style="{ left: toggleBtnLeft }"
      @click="toggleChatsList"
    >
      <v-icon>
        {{ showChatsList ? 'mdi-chevron-left' : 'mdi-chevron-right' }}
      </v-icon>
    </v-btn>

    <v-card class="fill-height d-flex flex-column">
      <v-layout
        class="flex-grow-1 fill-height"
        style="height: 100%"
      >
        <v-navigation-drawer
          v-if="!isSharedChat && isInitialized"
          v-model="showChatsList"
          location="left"
          :permanent="false"
          :scrim="false"
          :temporary="false"
          :width="drawerWidth"
        >
          <ChatsList />
        </v-navigation-drawer>

        <v-main
          :class="{ 'overflow-hidden': isFixedHeightLayout }"
          class="main-panel d-flex flex-column"
        >
          <AppHeader />

          <!-- Show skeleton while loading (shared chat or initial load) -->
          <div
            v-if="showLoading"
            class="chat-scroll-area pa-6 flex-grow-1"
          >
            <ChatMessageSkeleton
              v-for="i in 5"
              :key="i"
            />
          </div>

          <!-- Actual Chat Content -->
          <div
            v-else
            class="chat-scroll-area flex-grow-1"
          >
            <router-view />
          </div>
        </v-main>
      </v-layout>
    </v-card>
  </template>
</template>

<script lang="ts" setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import {
    refreshChats,
    loadSharedChat,
    loadingSharedChat,
    clearChats,
    chats,
    selectChat,
    startNewChat,
  } from '@/interfaces/composables/useChatManager';
  import AppHeader from '@/interfaces/components/AppHeader.vue';
  import { ensureSession } from '@/interfaces/composables/auth/auth';
  import { authState } from '@/interfaces/composables/auth/authState';
  import { useDisplay } from 'vuetify';

  import { useRoute } from 'vue-router';

  const { width } = useDisplay();

  const route = useRoute();

  const showChatsList = ref(false);
  const isInitialized = ref(false);

  // Responsive: smaller drawer for screens under 1400px (11-inch laptops ~1366px)
  const isSmallScreen = computed(() => width.value < 1400);
  const drawerWidth = computed(() => (isSmallScreen.value ? 300 : 400));
  const toggleBtnLeft = computed(() =>
    showChatsList.value ? `${drawerWidth.value + 10}px` : '10px'
  );

  const isFixedHeightLayout = computed(
    () => route.path.startsWith('/chat') && !route.path.includes('/export')
  );

  function toggleChatsList() {
    showChatsList.value = !showChatsList.value;
  }

  // Make isSharedChat reactive to route query changes
  const isSharedChat = computed(() => !!route.query.sharedId);

  // Show loading state until initialized (prevents flash of wrong content)
  const showLoading = computed(
    () =>
      !isInitialized.value || (isSharedChat.value && loadingSharedChat.value)
  );

  async function initializeRegularSession() {
    const isValid = await ensureSession();

    if (!isValid) {
      authState.showLoginPopup.value = true;
      return;
    }

    try {
      await refreshChats();
    } catch {
      return;
    }
  }

  // Watch for leaving shared mode (sharedId removed from query)
  watch(
    () => route.query.sharedId,
    async (newSharedId, oldSharedId) => {
      if (oldSharedId && !newSharedId) {
        // Left shared mode - clear shared chat and initialize regular session
        clearChats();
        await initializeRegularSession();

        // Select the most recent chat, or start new if none exist
        if (chats.value.length > 0) {
          await selectChat(chats.value[0].id);
        } else {
          startNewChat();
        }
      }
    }
  );

  onMounted(async () => {
    // Always check authentication first - no guest mode in this app
    const isValid = await ensureSession();

    if (!isValid) {
      authState.showLoginPopup.value = true;
      isInitialized.value = true;
      return;
    }

    // User is authenticated - now handle shared chat or regular flow
    const sharedId = route.query.sharedId as string | undefined;

    if (sharedId) {
      loadingSharedChat.value = true;
      isInitialized.value = true;
      try {
        await loadSharedChat(sharedId);
      } catch {
        return;
      } finally {
        loadingSharedChat.value = false;
      }
      return;
    }

    // Regular session flow - load user's chats
    try {
      await refreshChats();
    } catch {
      // Even if refresh fails, mark as initialized so UI can render
    }
    isInitialized.value = true;
  });
</script>

<style lang="scss" scoped>
  .toggle-drawer-btn {
    position: absolute;
    top: 20px;
    z-index: 10;
    background-color: #cffafe;
    color: #0e7490;
    box-shadow: 0 1px 4px rgb(0 0 0 / 10%);
    transition: left 0.3s ease, background-color 0.2s ease;
  }

  .toggle-drawer-btn:hover {
    background-color: #a5f3fc;
  }

  .chat-scroll-area {
    min-height: 0;
    flex: 1;
    overflow: hidden;
  }

  .main-panel {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .overflow-hidden {
    overflow: hidden;
  }
</style>